import React, { useCallback, useEffect, useState } from 'react';

import classNames from 'classnames';

import Console from 'utility/Debug/Console';
import { getFormattedPrice, PriceFormatter } from 'utility/Format/Price';

import { PriceRange } from 'proto/Search/PriceRange';

import Diagram from './__partials/Diagram';
import SliderValue from './__partials/SliderValue';
import { NumberTuple } from './types';

import styles from './PriceSlider.module.scss';

import RangeSlider from '.';

interface PriceSliderProps {
    priceRange: PriceRange;
    initialMinPrice?: number;
    initialMaxPrice?: number;
    initialIndexTuple?: NumberTuple;
    minDistance?: number;
    customClass?: {
        Diagram?: string;
    };
    onChange?: (value: NumberTuple) => void;
    onReset?: () => void;
    disabled?: boolean;
}

const PriceSlider = ({
    priceRange,
    initialMinPrice = 0,
    initialMaxPrice = 0,
    initialIndexTuple = [0, priceRange.rangeList.length - 1],
    minDistance,
    customClass,
    onChange,
    onReset,
    disabled = false,
}: PriceSliderProps) => {
    const { rangeList } = priceRange;

    const [selectedIndexes, setSelectedIndexes] =
        useState<NumberTuple>(initialIndexTuple);

    const [priceMin, setPriceMin] = useState(initialMinPrice);
    const [priceMax, setPriceMax] = useState(initialMaxPrice);

    const onInputChange = useCallback(
        (inputValues: string[]) => {
            Console.info('PriceSlider onInputChange', inputValues);
            const startValue = Number(inputValues[0]);
            const endValue = Number(inputValues[1]);

            const minIndex = rangeList.findIndex(
                (price) => startValue >= price.min && startValue < price.max
            );
            const maxIndex = rangeList.findLastIndex(
                (price) => endValue >= price.max && endValue >= price.min
            );

            setSelectedIndexes([
                minIndex > -1 ? minIndex : 0,
                maxIndex > -1 ? maxIndex : 1,
            ]);
            // setPriceMin, Max
            setPriceMin(startValue);
            setPriceMax(endValue);
            onChange?.([startValue, endValue]);
        },
        [onChange, rangeList]
    );

    const onPriceChange = useCallback(
        (selection: NumberTuple, thumb: number) => {
            Console.info('PriceSlider onPriceChange', selection);
            // setPriceMin, Max
            if (rangeList.length > 0) {
                const [startIndex, endIndex] = selection;
                if (startIndex === endIndex) {
                    if (thumb === 0) {
                        startIndex !== selectedIndexes[0] &&
                            setPriceMin(rangeList[startIndex].max);
                        if (endIndex && endIndex !== selectedIndexes[1]) {
                            setPriceMax(rangeList[endIndex].max);
                        }
                    } else {
                        startIndex !== selectedIndexes[0] &&
                            setPriceMin(rangeList[startIndex].min);
                        if (endIndex && endIndex !== selectedIndexes[1]) {
                            setPriceMax(rangeList[endIndex].min);
                        }
                    }
                } else {
                    startIndex !== selectedIndexes[0] &&
                        setPriceMin(rangeList[startIndex].min);
                    if (endIndex && endIndex !== selectedIndexes[1]) {
                        setPriceMax(rangeList[endIndex].max);
                    }
                }
            }
        },
        [rangeList, selectedIndexes]
    );

    const onChange_ = useCallback(
        (selection: NumberTuple, index: number) => {
            Console.info('PriceSlider onChange_', selection);
            if (
                selection[0] === 0 &&
                rangeList.length &&
                selection[1] === rangeList.length - 1 &&
                selection !== initialIndexTuple
            ) {
                onReset?.();
            }

            setSelectedIndexes(selection);
            onPriceChange(selection, index);
        },
        [initialIndexTuple, onPriceChange, onReset, rangeList.length]
    );

    useEffect(() => {
        setSelectedIndexes(initialIndexTuple);
        setPriceMin(initialMinPrice);
        setPriceMax(initialMaxPrice);
    }, [initialIndexTuple, initialMaxPrice, initialMinPrice]);

    return (
        <div className={styles.PriceSliderContainer}>
            <Diagram
                list={rangeList.map((range) => range.count)}
                selectedIndexes={selectedIndexes}
                customClass={customClass}
                firstBarActive={initialMaxPrice >= priceRange.minPrice}
            />
            <RangeSlider
                value={selectedIndexes}
                min={0}
                max={rangeList.length - 1 < 1 ? 1 : rangeList.length - 1}
                minDistance={minDistance}
                onChange={onChange_}
                onAfterChange={(value, index) => {
                    Console.info('RangeSlider onAfterChange', value, index, [
                        priceMin,
                        priceMax,
                    ]);
                    if (value[0] === value[1]) {
                        if (index === 0) {
                            onChange?.([priceMax, priceMax]);
                        } else {
                            onChange?.([priceMin, priceMin]);
                        }
                    } else {
                        onChange?.([priceMin, priceMax]);
                    }
                }}
                forceActive={
                    initialMinPrice < priceRange.minPrice ||
                    initialMaxPrice > priceRange.maxPrice
                }
                customClass={{
                    Slider: classNames({
                        [styles.Disabled]: disabled,
                    }),
                }}
            />
            <SliderValue
                format={(value) =>
                    getFormattedPrice(value, PriceFormatter.NoFractionDigits)
                }
                value={[priceMin, priceMax]}
                onChange={onInputChange}
            />
        </div>
    );
};

export default PriceSlider;
